import * as React from 'react'

import { Icon } from '~/components/icon/Icon'
import { styled } from '~/styled'
import { overrideStyledVariantValue } from '~/utilities/override-styled-variant-value/overrideStyledVariantValue'

export const StyledChipContent = styled('span', {
  base: [
    'inline-flex',
    'items-center',
    'px-1',
    'grow',
    'overflow-hidden',
    'text-ellipsis',
    'whitespace-nowrap',
    '*:not-last:mr-1'
  ]
})

const toIconSize = { sm: 'sm', md: 'sm', lg: 'md' }

export const ChipIcon = (props: React.ComponentProps<typeof Icon>) => {
  const rootContext = React.useContext(ChipRootContext)
  const { size } = rootContext
  const iconSize = React.useMemo(
    () => overrideStyledVariantValue(size, (s) => toIconSize[s]),
    [size]
  )
  return <Icon {...props} size={iconSize} />
}

const ChipContent = ({ children, ...rest }) => {
  const childrenArray = React.Children.toArray(children)
  const isSingleChild = childrenArray.length <= 1
  return (
    <StyledChipContent {...rest}>
      {
        childrenArray.map((child, index) => {
          if (!isSingleChild && typeof child === 'string')
            return (
              <span
                key={child}
                className="overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {child}
              </span>
            )
          if (
            React.isValidElement<React.ComponentPropsWithoutRef<typeof Icon>>(
              child
            ) &&
            child.type === Icon
          ) {
            return <ChipIcon key={`icon-${index}`} {...child.props} />
          }
          return child
        }) as React.ReactElement<unknown>[]
      }
    </StyledChipContent>
  )
}

export const StyledRoot = styled('div', {
  base: [
    'flex',
    'px-2',
    'border',
    'rounded-md',
    'items-center',
    'font-body',
    'max-w-full',
    'border-primary-800',
    'text-primary-900',
    'bg-primary-100',
    'disabled:opacity-30',
    'disabled:pointer-events-none'
  ],
  variants: {
    size: {
      sm: ['h-6', 'text-sm', 'leading-[1.53]', 'capsize-[0.4056]'],
      md: ['h-8', 'text-sm', 'leading-[1.53]', 'capsize-[0.4056]'],
      lg: ['h-10', 'text-md', 'leading-normal', 'capsize-[0.3864]']
    },
    appearance: {
      standard: [],
      modern: ['border-grey-900', 'text-grey-900', 'bg-grey-100']
    }
  }
})

export type TChipRootContext = React.ComponentProps<typeof StyledRoot>
export type TChipRootProviderProps = TChipRootContext

export const ChipRootContext = React.createContext<TChipRootContext>({})

export const ChipRootProvider = ({
  size,
  children
}: TChipRootProviderProps) => {
  const value = React.useMemo<TChipRootContext>(() => ({ size }), [size])
  return (
    <ChipRootContext.Provider value={value}>
      {children}
    </ChipRootContext.Provider>
  )
}

export type TChipRootProps = TChipRootProviderProps

const ChipRoot = React.forwardRef<HTMLDivElement, TChipRootProps>(
  ({ size = 'md', ...rest }, ref) => {
    return (
      <ChipRootProvider size={size}>
        <StyledRoot ref={ref} size={size} {...rest} />
      </ChipRootProvider>
    )
  }
)

export const Chip = Object.assign(ChipRoot, {
  Content: ChipContent,
  Icon: ChipIcon
})

ChipRoot.displayName = 'Chip'
